// Daily Relationship Tips for Relationship Compass
// Warm, research-backed insights aligned with the 8 modules
// Australian English throughout

const relationshipTips = [
  {
    id: 1,
    category: "Appreciation & Connection",
    tip: "Gottman found that couples who stayed together showed about five positive interactions for every negative one when they disagreed. One criticism takes several appreciations to balance.",
    actionable: "Today, express one specific appreciation using the three-part formula: behaviour + impact + value. 'When you [behaviour], it makes me feel [impact] because it shows [value].'"
  },
  {
    id: 2,
    category: "Appreciation & Connection",
    tip: "The negativity bias means your brain tends to remember criticism more vividly than praise. Several positives aren't overkill; they're what it takes to counteract one negative.",
    actionable: "Notice something your partner does well today that you usually take for granted. Acknowledge it specifically rather than letting it pass unmarked."
  },
  {
    id: 3,
    category: "Appreciation & Connection",
    tip: "Shelly Gable's research on 'capitalisation' found that how partners respond to each other's good news is more closely linked to relationship well-being than how they respond to bad news. Active, enthusiastic responding strengthens bonds.",
    actionable: "When your partner shares something positive today, match their energy. Show genuine interest. Ask questions. Celebrate with them rather than minimising or redirecting."
  },
  {
    id: 4,
    category: "Emotional Intelligence",
    tip: "Our Feeling Wheel has over 130 emotion words, yet most of us reach for the same few: 'fine', 'good', 'stressed'. Emotional precision creates better understanding and connection.",
    actionable: "Next time you feel something, go beyond 'fine,' 'good,' or 'stressed.' Name the specific emotion. Worried? Disappointed? Excited? Overwhelmed? Precision helps your partner understand."
  },
  {
    id: 5,
    category: "Emotional Intelligence",
    tip: "Validation doesn't mean agreement. You can understand someone's perspective and emotional experience whilst having a different view. 'That makes sense' isn't surrender. It's connection.",
    actionable: "When your partner expresses emotion today, validate before problem-solving: 'I can see why you'd feel that way' or 'That sounds really frustrating.' Understanding first, solutions second."
  },
  {
    id: 6,
    category: "Emotional Intelligence",
    tip: "Attunement is noticing and responding to your partner's emotional state, even when they haven't said anything. It's the quiet 'You okay?' when something's off.",
    actionable: "Watch for subtle shifts in your partner's energy, tone, or body language today. Check in when you sense something: 'You seem quiet, everything alright?'"
  },
  {
    id: 7,
    category: "Communication",
    tip: "Gottman's Four Horsemen (criticism, contempt, defensiveness, stonewalling) were among the strongest predictors of divorce in his research. Contempt (mockery, sarcasm, eye-rolling) was the strongest of all.",
    actionable: "Catch yourself if you use criticism today ('You always/never...') and replace with gentle startup: 'I feel [emotion] about [situation]. I need [specific request].'"
  },
  {
    id: 8,
    category: "Communication",
    tip: "Intent ≠ Impact. You can mean well and still hurt someone. The gap between what you meant and how it landed is where conflict lives.",
    actionable: "If your partner reacts negatively to something you said today, resist 'But I didn't mean it that way!' Try: 'I'm sorry that landed poorly. What did you hear? I want to understand.'"
  },
  {
    id: 9,
    category: "Communication",
    tip: "Active listening (LAER: Listen, Acknowledge, Explore, Respond) is a skill, not a talent. It's easy to listen to reply rather than to understand.",
    actionable: "In one conversation today, focus entirely on understanding before responding. Paraphrase what you heard: 'So you're saying...' and check if you got it right."
  },
  {
    id: 10,
    category: "Conflict Resolution",
    tip: "Gottman estimates that 69% of conflicts are perpetual: personality or value differences that never fully resolve. These need ongoing dialogue, not solutions. Fighting the wrong battle creates frustration.",
    actionable: "Think about your recurring conflicts. Are you trying to 'solve' a perpetual problem? If so, shift to understanding the underlying values and finding compromise."
  },
  {
    id: 11,
    category: "Conflict Resolution",
    tip: "Gottman treats a heart rate above about 100bpm during conflict as a sign of flooding. At that point, calm conversation becomes very hard. Taking breaks prevents damage, but you must return to discuss.",
    actionable: "If conflict escalates today, notice your body: racing heart, tight chest, tunnel vision. Call a timeout: 'I'm flooded. Let's take 20 minutes and come back to this.'"
  },
  {
    id: 12,
    category: "Conflict Resolution",
    tip: "Repair attempts (small gestures that de-escalate) are crucial. 'That came out wrong,' 'Can we start over?' or even humour can interrupt negativity spirals.",
    actionable: "If tension rises today, try a repair: 'I'm sorry, I'm not handling this well' or 'Can we pause and try again?' Watch if your partner accepts the repair offer."
  },
  {
    id: 13,
    category: "Intimacy & Vulnerability",
    tip: "Intimacy isn't just physical. It's emotional, intellectual, experiential, and spiritual too. Many couples maintain physical connection whilst other dimensions drift.",
    actionable: "Which intimacy dimension feels neglected? Emotional (being known)? Intellectual (sharing ideas)? Experiential (doing things together)? Pick one to nurture today."
  },
  {
    id: 14,
    category: "Intimacy & Vulnerability",
    tip: "Vulnerability creates connection, but pace it. Share a little at a time, watch for reciprocity, and share with someone who has earned it.",
    actionable: "Share something slightly vulnerable today: a fear, a hope, something you don't usually voice. Notice if your partner reciprocates or holds space for you."
  },
  {
    id: 15,
    category: "Intimacy & Vulnerability",
    tip: "Desire discrepancy is normal. In many couples one partner wants sex more often than the other. It's negotiable when both partners feel understood rather than pressured or rejected.",
    actionable: "If desire difference exists, have a non-bedroom conversation: 'How can we honour both our needs? What would help you feel desire? What would help you feel less pressure?'"
  },
  {
    id: 16,
    category: "Trust",
    tip: "Relationship Compass breaks trust into five components: transparency, reliability, attunement, non-defensiveness, and selective sharing. It's built through small consistent actions, not grand gestures.",
    actionable: "Keep one small promise today, even something tiny. Be where you said you'd be. Follow through on what you committed to. Trust is built in the boring moments."
  },
  {
    id: 17,
    category: "Trust",
    tip: "Trust erodes slowly through accumulated small betrayals. One forgotten promise is forgivable; thirty forgotten promises destroy the foundation.",
    actionable: "Reflect: Are there small commitments you've let slide? Text responses? Shared responsibilities? Small course corrections prevent major breaches."
  },
  {
    id: 18,
    category: "Trust",
    tip: "After major trust breach, repair takes years, not months. Both partners must fully commit. There's no shortcut. Some betrayals can't or shouldn't be repaired.",
    actionable: "If trust has been broken, ask honestly: Are both of us fully committed to rebuilding? Am I demonstrating trustworthy behaviour consistently? Is repair actually happening?"
  },
  {
    id: 19,
    category: "Growth",
    tip: "Healthy relationships balance togetherness with individual growth. Codependency (merged identity) isn't intimacy. You need solid sense of self whilst deeply connected.",
    actionable: "What's one individual interest or goal you've neglected? Make time for it this week. Your growth enriches the relationship, it doesn't threaten it."
  },
  {
    id: 20,
    category: "Growth",
    tip: "Major life transitions (new baby, career change, relocation, loss) test relationships. Couples who navigate them as a team grow closer. Those who face them separately drift.",
    actionable: "If you're facing any transition, use 'us versus the problem' framing. Ask your partner: 'How can we face this together? What support do you need from me?'"
  },
  {
    id: 21,
    category: "Growth",
    tip: "Shared vision matters more than you think. Couples aligned on major life questions (kids, career, lifestyle, values) navigate challenges better than those winging it.",
    actionable: "Have you discussed your vision for 5 years from now? 10 years? Where you want to live, work-life balance, family goals? If not, start that conversation."
  },
  {
    id: 22,
    category: "Resilience",
    tip: "Thriving relationships require maintenance across multiple time scales: daily connection moments, weekly check-ins, monthly temperature checks, annual reviews.",
    actionable: "What's your current maintenance rhythm? Daily appreciation? Weekly date night? Monthly check-in? Pick one ritual to establish if you don't have it yet."
  },
  {
    id: 23,
    category: "Resilience",
    tip: "Small consistent practices beat occasional grand gestures. The couples happy decades in aren't lucky. They've maintained boring daily practices that accumulate.",
    actionable: "Today, do one small boring practice: six-second kiss goodbye, specific appreciation, five-minute check-in. These aren't romantic but they're what works."
  },
  {
    id: 24,
    category: "Resilience",
    tip: "In Gottman's six-year study of newlyweds, couples who stayed married had turned towards each other's bids for connection about 86% of the time; couples who divorced, about 33%. Turning away creates distance.",
    actionable: "When your partner makes a bid today (shares something, asks a question, seeks connection), turn toward it. Put down your phone. Make eye contact. Engage."
  },
  {
    id: 25,
    category: "Appreciation & Connection",
    tip: "Receiving appreciation matters as much as giving it. Deflecting ('Oh, it was nothing') or minimising ('Anyone would do that') breaks the connection loop.",
    actionable: "When someone appreciates you today, practise receiving: 'Thank you, that means a lot' or 'I'm glad that mattered to you.' Let it land rather than deflecting."
  },
  {
    id: 26,
    category: "Communication",
    tip: "'Softened startup' means beginning difficult conversations gently. Gottman reports that the first three minutes predict how a conflict conversation ends 96% of the time. Harsh startup predicts harsh ending.",
    actionable: "If you need to raise a concern today, start soft: 'I'm feeling [emotion] and I'd like to talk about [situation]' rather than 'You always...' or 'Why don't you ever...'"
  },
  {
    id: 27,
    category: "Emotional Intelligence",
    tip: "Emotional flooding isn't weakness. It's physiology. Your body's fight-or-flight response kicks in, and thinking clearly gets very hard. Breaks aren't avoidance; they're necessary.",
    actionable: "Agree on a flooding signal with your partner: 'I need a break' or 'I'm flooded.' Commit to 20-30 minutes minimum before returning to discuss."
  },
  {
    id: 28,
    category: "Conflict Resolution",
    tip: "Stonewalling (shutting down, silent treatment, walking away) is a horseman that kills relationships. If you do this, you're likely flooding. Learn to take proper timeouts instead.",
    actionable: "If you tend to shut down during conflict, practise saying: 'I'm overwhelmed and need space to calm down. Can we revisit this in 30 minutes?' Then return."
  },
  {
    id: 29,
    category: "Trust",
    tip: "Transparency doesn't mean no privacy. It means no secrets. You can have private thoughts without maintaining hidden parts of your life your partner doesn't know about.",
    actionable: "Reflect: Is there anything I'm keeping from my partner that, if discovered, would hurt them? If yes, consider whether transparency would strengthen or damage trust."
  },
  {
    id: 30,
    category: "Resilience",
    tip: "Positive sentiment override means you give your partner the benefit of the doubt when behaviour is ambiguous. Negative sentiment override means you assume the worst. Which one you're in colours everything else.",
    actionable: "When your partner does something neutral or slightly negative today, notice your first thought. Are you assuming positive intent ('They're stressed') or negative ('They don't care')?"
  }
];

// Function to get tip of the day (deterministic based on date)
export const getTipOfTheDay = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  const tipIndex = dayOfYear % relationshipTips.length;
  return relationshipTips[tipIndex];
};

export default relationshipTips;
